// src/components/profile/ProfileForm.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Grid,
  Paper,
  Divider,
  InputAdornment,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Work as WorkIcon,
  LocationOn as LocationIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

const ProfileForm = () => {
  const { user, updateProfile, loading } = useAuth();
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    phone: '',
    department: '',
    location: '',
    bio: '',
    specialty: ''
  });
  const [originalData, setOriginalData] = useState({});
  const [updating, setUpdating] = useState(false);
  const [message, setMessage] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Inicializar formulario con datos del usuario
  useEffect(() => {
    if (user) {
      const initialData = {
        displayName: user.displayName || '',
        email: user.email || '',
        phone: user.phone || '',
        department: user.department || '',
        location: user.location || '',
        bio: user.bio || '',
        specialty: user.specialty || ''
      };
      setFormData(initialData);
      setOriginalData(initialData);
    }
  }, [user]);

  // Verificar si hay cambios
  useEffect(() => {
    const hasChanges = Object.keys(formData).some(
      key => formData[key] !== originalData[key]
    );
    setHasChanges(hasChanges);
  }, [formData, originalData]);

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    // Limpiar mensaje cuando el usuario empiece a escribir
    if (message) setMessage(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!hasChanges) {
      setMessage({ type: 'info', text: 'No hay cambios para guardar' });
      return;
    }

    setUpdating(true);
    setMessage(null);

    try {
      // Preparar datos solo con los campos que cambiaron
      const changedData = {};
      Object.keys(formData).forEach(key => {
        if (formData[key] !== originalData[key]) {
          changedData[key] = formData[key].trim();
        }
      });

      await updateProfile(changedData);
      
      // Actualizar los datos originales
      setOriginalData(formData);
      setMessage({ 
        type: 'success', 
        text: 'Perfil actualizado correctamente' 
      });

      // Limpiar mensaje después de 5 segundos
      setTimeout(() => setMessage(null), 5000);

    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.message || 'Error al actualizar el perfil' 
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleReset = () => {
    setFormData(originalData);
    setMessage(null);
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const userRole = user?.rol || user?.role;

  return (
    <Box>
      <Typography variant="h6" gutterBottom fontWeight="bold">
        Información Personal
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Actualiza tu información personal y profesional
      </Typography>

      {/* Mostrar rol actual */}
      {userRole && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Rol actual:
          </Typography>
          <Chip 
            label={userRole.charAt(0).toUpperCase() + userRole.slice(1)} 
            color="primary" 
            variant="outlined" 
          />
        </Box>
      )}

      <Divider sx={{ mb: 3 }} />

      {/* Mensaje de estado */}
      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }}>
          {message.text}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Información básica */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Información Básica
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Nombre completo"
              value={formData.displayName}
              onChange={handleInputChange('displayName')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="action" />
                  </InputAdornment>
                ),
              }}
              helperText="Tu nombre completo como aparecerá en el sistema"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleInputChange('email')}
              error={formData.email && !validateEmail(formData.email)}
              helperText={
                formData.email && !validateEmail(formData.email)
                  ? "Ingresa un email válido"
                  : "Tu dirección de correo electrónico"
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
              disabled // El email no debe ser editable en muchos casos
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Teléfono"
              value={formData.phone}
              onChange={handleInputChange('phone')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon color="action" />
                  </InputAdornment>
                ),
              }}
              helperText="Tu número de teléfono de contacto"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Ubicación"
              value={formData.location}
              onChange={handleInputChange('location')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationIcon color="action" />
                  </InputAdornment>
                ),
              }}
              helperText="Ciudad, estado o país"
            />
          </Grid>

          {/* Información profesional */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Información Profesional
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Departamento"
              value={formData.department}
              onChange={handleInputChange('department')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <WorkIcon color="action" />
                  </InputAdornment>
                ),
              }}
              helperText="Departamento o área de trabajo"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Especialidad"
              value={formData.specialty}
              onChange={handleInputChange('specialty')}
              helperText="Tu especialidad o área de expertise"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Biografía"
              value={formData.bio}
              onChange={handleInputChange('bio')}
              helperText="Cuéntanos un poco sobre ti (opcional)"
            />
          </Grid>

          {/* Botones de acción */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleReset}
                disabled={!hasChanges || updating}
                startIcon={<CancelIcon />}
              >
                Descartar cambios
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={!hasChanges || updating || (formData.email && !validateEmail(formData.email))}
                startIcon={updating ? <CircularProgress size={16} /> : <SaveIcon />}
              >
                {updating ? 'Guardando...' : 'Guardar cambios'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ProfileForm;